import { Divider, styled, Typography } from '@mui/material';
import { Row } from 'components/Layout';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import { CurrencyTransactionOperation } from './types';
import { Card, CardContent } from 'further-ui/components/Card';
import { Column } from 'further-ui/components/Layout';

const FullHeightCard = styled(Card)({
  height: '100%',
});

const FullHeightCardContent = styled(CardContent)(({ theme }) => ({
  height: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.sm),
}));

const CardTop = styled(Column)({
  flex: 1,
});

type Props = {
  income: number;
  costs: number;
  total: number;
  bankTransactionAmount?: number;
  currency: CurrencyCode;
  isTransfer: boolean;
  operation: CurrencyTransactionOperation;
};

const CurrencyTransactionSummary: React.FC<Props> = ({
  operation,
  currency,
  isTransfer,
  income,
  costs,
  total,
  bankTransactionAmount,
}) => {
  const isStatementCurrency = !!bankTransactionAmount;
  const isCurrencySale = operation === CurrencyTransactionOperation.Sale;

  const transactionOperationName = isTransfer ? 'Transfer' : 'Payment';

  const headingText = isStatementCurrency
    ? 'Statement currency'
    : `${transactionOperationName} currency`;

  const values = isStatementCurrency
    ? [
        {
          label: 'Bank statement amount',
          value: bankTransactionAmount,
        },
        {
          label: isCurrencySale
            ? `Plus ${currency} incoming`
            : `Plus ${currency} ${isTransfer ? 'costs' : 'expenses'}`,
          value: isCurrencySale ? income : costs,
        },
        ...(!isTransfer
          ? [
              {
                label: isCurrencySale
                  ? `Minus ${currency} outgoing`
                  : `Minus ${currency} income`,
                value: isCurrencySale ? costs : income,
              },
            ]
          : []),
      ]
    : [
        {
          label: isCurrencySale ? `${currency} income` : `${currency} expenses`,
          value: isCurrencySale ? income : costs,
        },
        {
          label: isCurrencySale
            ? isTransfer
              ? `Minus ${currency} costs`
              : `${currency} outgoing`
            : `${currency} income`,
          value: isCurrencySale ? costs : income,
        },
      ];

  const summaryText = isStatementCurrency
    ? `Net ${transactionOperationName.toLowerCase()} amount ${currency}`
    : `Net ${currency} amount ${isCurrencySale ? 'incoming' : 'outgoing'}`;

  return (
    <Column fullWidth>
      <FullHeightCard variant="outlined">
        <FullHeightCardContent size="sm">
          <CardTop fullWidth spacing="sm">
            <Typography variant="h6">{headingText}</Typography>
            <Divider />
            <Column spacing="xs">
              {values.map((value) => (
                <Row
                  centered
                  fullWidth
                  justify="space-between"
                  spacing="sm"
                  key={value.label}
                >
                  <Typography variant="body2">{value.label}</Typography>
                  <Typography variant="body2">
                    {numberToCurrencyString(value.value, {
                      currency,
                    })}
                  </Typography>
                </Row>
              ))}
            </Column>
          </CardTop>
          <Column fullWidth spacing="sm">
            <Divider />
            <Row centered fullWidth justify="space-between" spacing="sm">
              <Typography variant="h6" fontWeight="bold">
                {summaryText}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {numberToCurrencyString(total, {
                  currency,
                })}
              </Typography>
            </Row>
          </Column>
        </FullHeightCardContent>
      </FullHeightCard>
    </Column>
  );
};

export default CurrencyTransactionSummary;
